import * as React from 'react'

import { Smartphone } from 'lucide-react'

import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import { IPhoneClaimConflict } from '~/client/src/shared/utils/bulkSaveErrorFormatter'

interface IProps {
  // Non-null opens the dialog; null keeps it closed.
  conflict: IPhoneClaimConflict | null
  targetName: string
  onCancel: () => void
  // Confirm: re-save with the reassign flag set. The backend moves the number
  // (from whichever account holds it, any tenant) onto this row's user as part
  // of the save; the number then arrives in the UI via the WS DB feed.
  onConfirm: () => void
}

/**
 * Shown to an admin when saving a user hits a phone number already held by
 * someone else (STX1-637). Confirming reassigns the number to the user being
 * saved (create or edit) — same intent either way.
 */
export default function PhoneReassignConfirm({
  conflict,
  targetName,
  onCancel,
  onConfirm,
}: IProps): React.ReactElement {
  // Portal into #app so this layers above the (also #app-portaled) member dialog.
  const portalContainer = React.useRef<HTMLElement | null>(null)
  if (!portalContainer.current) {
    portalContainer.current = document.getElementById('app')
  }

  const holder = conflict?.holderName || 'another user'

  return (
    <Dialog
      open={!!conflict}
      modal="trap-focus"
      onOpenChange={open => {
        if (!open) onCancel()
      }}
    >
      <DialogContent className="tw:w-[440px]" portalContainer={portalContainer}>
        <DialogHeader>
          <DialogTitle className="tw:flex tw:items-center tw:gap-2">
            <Smartphone className="tw:size-5 tw:text-primary" />
            Phone number already assigned
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="tw:flex tw:flex-col tw:gap-4">
          <p className="tw:text-body-sm tw:text-muted-foreground">
            {conflict?.phoneNumber} is currently assigned to{' '}
            <span className="tw:font-medium tw:text-foreground">{holder}</span>.
            Reassign it to{' '}
            <span className="tw:font-medium tw:text-foreground">
              {targetName}
            </span>{' '}
            and save? The current holder will lose this number — and, if it is
            their only way to sign in, access to StruxHub.
          </p>

          <div className="tw:flex tw:justify-end tw:gap-2">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={onConfirm}>Reassign and save</Button>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
